import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Loader2, Medal } from "lucide-react";
import { gamesApi } from "@/lib/api";
import { cn } from "@/lib/utils";

const DUCK_TIERS = [
  { key: "easy", label: "Easy" },
  { key: "normal", label: "Normal" },
  { key: "hard", label: "Hard" },
];

const RANK_STYLES = ["text-amber-500", "text-slate-400", "text-amber-700"];

// All-time high-score board. Duck Hunt gets per-difficulty tabs; every other game shows one board.
export default function Leaderboard({ game, phone = "", title = "Leaderboard" }) {
  const isDuck = game === "duck";
  const [difficulty, setDifficulty] = useState("normal");
  const diff = isDuck ? difficulty : "";

  const q = useQuery({
    queryKey: ["leaderboard", game, diff],
    queryFn: () => gamesApi.leaderboard(game, { difficulty: diff, phone, limit: 10 }),
  });
  const data = q.data;

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="leaderboard">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-sm font-bold">
          <Trophy className="h-4 w-4 text-amber-500" /> {title}
        </h2>
        {isDuck ? (
          <div className="flex gap-1 rounded-full bg-secondary p-0.5">
            {DUCK_TIERS.map((t) => (
              <button
                key={t.key}
                onClick={() => setDifficulty(t.key)}
                data-testid={`leaderboard-tier-${t.key}`}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                  difficulty === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-3">
        {q.isPending ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : !data?.entries?.length ? (
          <p className="py-5 text-center text-sm text-muted-foreground">No scores yet — play a round to claim the top spot! 🏆</p>
        ) : (
          <ol className="space-y-1.5" data-testid="leaderboard-list">
            {data.entries.map((e) => (
              <li key={e.rank} className="flex items-center gap-3 rounded-xl bg-secondary/60 px-3 py-2">
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center font-display text-sm font-bold", RANK_STYLES[e.rank - 1] || "text-muted-foreground")}>
                  {e.rank <= 3 ? <Medal className="h-4 w-4" /> : e.rank}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{e.name}</span>
                <span className="font-display text-sm font-bold text-primary">{e.score}</span>
              </li>
            ))}
          </ol>
        )}
        {data?.me ? (
          <p className="mt-3 rounded-xl bg-[hsl(var(--brand-tertiary))] px-3 py-2 text-center text-xs font-semibold text-primary" data-testid="leaderboard-me">
            Your best: {data.me.score} pts · Rank #{data.me.rank}
          </p>
        ) : null}
      </div>
    </div>
  );
}
